/**
 * Users API Hook
 * 
 * Provides hooks for managing users with demo/live mode support
 * Handles API integration when backend is available
 */

import { useState, useCallback } from 'react';
import { useMode } from '@/providers/ModeProvider';
import { demoUsers } from '@/data/demo-teams';
import type { User, UserFilters } from '@/types/teams';
import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import { getBackendUrl } from '@/hooks/use-get-backend-url';

// Helper function to filter users by search query - extracted to reduce cognitive complexity (S3776)
function filterUsersBySearch(users: User[], search: string): User[] {
  const searchLower = search.toLowerCase();
  return users.filter(
    user =>
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
  );
}

// Helper function to filter users by team ID - extracted to reduce cognitive complexity (S3776)
function filterUsersByTeam(users: User[], teamId: string): User[] {
  return users.filter(user =>
    user.teams.some(team => {
      const id = typeof team === 'string' ? team : team.teamId;
      return id === teamId;
    })
  );
}

/**
 * Get the API base URL using the same logic as DataService.
 * Checks configured URL, env variable, and backend URL detection.
 */
const getApiBaseUrl = (): string => {
  const apiUrlFromConfig = getApiUrl();
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  const backendUrl = getBackendUrl();
  const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

  // In E2B environment, always use the detected backend URL
  // Otherwise, use the configured URL or env URL as fallback
  const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);
  return baseUrl.replace(/\/$/, '');
};

export interface RefreshUsersResponse {
  totalCount: number;
  newUsers: number;
  updatedUsers: number;
  syncedAt: string;
}

export function useUsers() {
  const { isDemo } = useMode();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Load users based on mode
  const loadUsers = useCallback(async (filters?: UserFilters) => {
    setLoading(true);
    setError(null);

    try {
      if (isDemo) {
        // Demo mode - use local data
        await new Promise(resolve => setTimeout(resolve, 300));
        let filteredUsers = [...demoUsers];

        // Apply filters using extracted helper functions to reduce cognitive complexity (S3776)
        if (filters?.search) {
          filteredUsers = filterUsersBySearch(filteredUsers, filters.search);
        }

        if (filters?.teamId) {
          filteredUsers = filterUsersByTeam(filteredUsers, filters.teamId);
        }

        setUsers(filteredUsers);
      } else {
        // Live mode - fetch from API
        const queryParams = new URLSearchParams();
        if (filters?.search) queryParams.append('search', filters.search);
        if (filters?.teamId) queryParams.append('teamId', filters.teamId);

        const response = await authFetch(
          `${getApiBaseUrl()}/api/users?${queryParams.toString()}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch users from API');
        }

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('API returned non-JSON response. The backend may not be running or the endpoint may not exist.');
        }

        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      // Fallback to demo data on error
      setUsers(demoUsers);
    } finally {
      setLoading(false);
    }
  }, [isDemo]);

  // Get user by ID
  const getUser = useCallback(
    async (userId: string): Promise<User | null> => {
      if (isDemo) {
        // Demo mode - find in local data
        return users.find(user => user.id === userId) ?? null;
      } else {
        // Live mode - fetch from API
        const response = await authFetch(`${getApiBaseUrl()}/api/users/${userId}`);

        if (!response.ok) {
          throw new Error('Failed to fetch user');
        }

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('API returned non-JSON response');
        }

        return await response.json();
      }
    },
    [isDemo, users]
  );

  // Refresh users from Workspace API
  const refreshUsers = useCallback(
    async (organisationId?: string): Promise<RefreshUsersResponse | null> => {
      if (isDemo) {
        // Demo mode - simulate refresh
        await new Promise(resolve => setTimeout(resolve, 1000));
        return {
          totalCount: demoUsers.length,
          newUsers: 0,
          updatedUsers: demoUsers.length,
          syncedAt: new Date().toISOString(),
        };
      }

      setRefreshing(true);
      setError(null);

      try {
        const response = await authFetch(`${getApiBaseUrl()}/api/users/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ organisationId }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to refresh users from Workspace API');
        }

        const result: RefreshUsersResponse = await response.json();
        return result;
      } catch (err) {
        console.error('Error refreshing users:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        return null;
      } finally {
        setRefreshing(false);
      }
    },
    [isDemo]
  );

  return {
    users,
    loading,
    error,
    refreshing,
    loadUsers,
    getUser,
    refreshUsers,
  };
}
